import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, timer } from 'rxjs';
import {
  tap,
  catchError,
  finalize,
  filter,
  take,
  map,
  retry,
} from 'rxjs/operators';
import { AuthStateService } from './auth-state.service';
import { LocalStorageService } from '../infrastructure/storage/local-storage.service';
import { LoggerService } from './logger.service';

/**
 * Service responsible for refreshing JWT access tokens.
 *
 * Handles automatic token refresh using the refresh token cookie.
 * Prevents race conditions when multiple requests trigger refresh simultaneously.
 *
 * @example
 * ```typescript
 * // Check if refresh is needed
 * if (this.refreshTokenService.shouldRefresh() && this.refreshTokenService.canRefresh()) {
 *   this.refreshTokenService.refreshToken().subscribe();
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class RefreshTokenService {
  /**
   * Observable to track if a refresh operation is currently in progress.
   * Used to prevent concurrent refresh requests.
   */
  private readonly refreshInProgress$ = new BehaviorSubject<boolean>(false);

  /**
   * Timestamp of the last successful refresh.
   * Used to prevent too frequent refresh attempts.
   */
  private lastRefreshTime = 0;

  /**
   * Minimum interval between refresh attempts (in milliseconds).
   * Prevents excessive refresh requests.
   */
  private readonly minRefreshInterval = 5000; // 5 seconds

  /**
   * Time before token expiration to trigger a refresh (in milliseconds).
   * Refreshes token 5 minutes before it expires.
   */
  private readonly refreshThreshold = 5 * 60 * 1000; // 5 minutes

  constructor(
    private readonly http: HttpClient,
    private readonly authState: AuthStateService,
    private readonly storage: LocalStorageService,
    private readonly logger: LoggerService,
  ) {}

  /**
   * Get the observable tracking refresh progress.
   * Useful for showing loading indicators during refresh.
   */
  get refreshInProgress(): Observable<boolean> {
    return this.refreshInProgress$.asObservable();
  }

  /**
   * Check if a token refresh should be performed.
   *
   * Returns true if:
   * - User is authenticated
   * - Enough time has passed since last refresh (MIN_REFRESH_INTERVAL)
   * - Token is close to expiration (within REFRESH_THRESHOLD)
   *
   * @returns true if refresh should be performed
   */
  shouldRefresh(): boolean {
    if (!this.authState.isAuthenticated()) {
      return false;
    }

    const now = Date.now();

    // Prevent too frequent refreshes
    if (now - this.lastRefreshTime < this.minRefreshInterval) {
      this.logger.debug('Refresh skipped: too soon after last refresh');
      return false;
    }

    const expiresAt = this.authState.getExpiration();
    if (!expiresAt) {
      // Fallback if no expiration stored (e.g. old session)
      // We rely on 401 interceptor in this case
      return false;
    }

    // Check if within threshold
    if (now > expiresAt - this.refreshThreshold) {
      this.logger.debug('Token close to expiration, refreshing...', {
        expiresAt: new Date(expiresAt).toISOString(),
        threshold: this.refreshThreshold,
      });
      return true;
    }

    return false;
  }

  /**
   * Check if token refresh is possible.
   *
   * Returns true if user opted for "keep me connected" option.
   * Without this option, refresh token is not stored.
   *
   * @returns true if refresh token is available
   */
  canRefresh(): boolean {
    const keepConnection = this.storage.getItem<boolean>('keepConnection');
    return keepConnection === true;
  }

  /**
   * Refresh the access token using the refresh token cookie.
   *
   * If a refresh is already in progress, waits for it to complete
   * instead of starting a new one (prevents race conditions).
   *
   * The backend will:
   * 1. Read refresh token from httpOnly cookie
   * 2. Validate it
   * 3. Generate new access token
   * 4. Set new access token in httpOnly cookie
   * 5. Optionally rotate refresh token (if configured)
   *
   * @returns Observable that emits true on success, throws error on failure
   */
  refreshToken(): Observable<boolean> {
    // If refresh already in progress, wait for it
    if (this.refreshInProgress$.value) {
      this.logger.debug('Refresh already in progress, waiting...');
      return this.refreshInProgress$.pipe(
        filter((inProgress) => !inProgress),
        take(1),
        map(() => true),
      );
    }

    this.logger.debug('Starting token refresh');
    this.refreshInProgress$.next(true);
    this.lastRefreshTime = Date.now();

    return this.http
      .post<{ token?: string; refreshToken?: string }>(
        '/token/refresh',
        {},
        {
          // CRITICAL: withCredentials sends the refresh token cookie
          withCredentials: true,
        },
      )
      .pipe(
        // Retry on network errors (with exponential backoff)
        retry({
          count: 2,
          delay: (error, retryCount) => {
            this.logger.warn(`Refresh retry ${retryCount} after error`, error);
            return timer(Math.pow(2, retryCount) * 1000);
          },
          resetOnSuccess: true,
        }),
        tap(() => {
          this.logger.info('Token refreshed successfully');
          // Backend automatically sets new cookies via JwtCookieEventSubscriber
          // No need to manually handle tokens on frontend
        }),
        map(() => true),
        catchError((error) => {
          this.logger.error('Token refresh failed', {
            status: error.status,
            message: error.message,
          });
          return throwError(() => error);
        }),
        finalize(() => {
          this.refreshInProgress$.next(false);
        }),
      );
  }

  /**
   * Clear refresh state.
   * Called on logout to reset the service.
   */
  clear(): void {
    this.refreshInProgress$.next(false);
    this.lastRefreshTime = 0;
    this.logger.debug('Refresh token service cleared');
  }
}
